import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services/apiError'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  type FetchCache,
} from '@/store/fetchCache'

import { decisionsService } from '../services/decisionsService.http'
import type {
  DecisionBlocked,
  DecisionDecideInput,
  DecisionFeatureError,
  DecisionOperation,
  DecisionSupersedeInput,
  DecisionView,
} from '../types'

interface ProjectArg {
  orgId: string
  projectId: string
}

const cacheKeyOf = ({ orgId, projectId }: ProjectArg): string =>
  `${orgId}|${projectId}`

const isDecisionFeatureError = (
  error: unknown
): error is DecisionFeatureError => {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as Partial<DecisionFeatureError>
  return (
    (candidate.operation === 'list' ||
      candidate.operation === 'decide' ||
      candidate.operation === 'supersede') &&
    typeof candidate.status === 'number' &&
    typeof candidate.message === 'string'
  )
}

const toDecisionError = (
  error: unknown,
  operation: DecisionOperation,
  scope?: string
): DecisionFeatureError => {
  // A rejected authoritative reread already carries the exact list-level
  // contract. Do not remap it as the action that caused the reread: doing so
  // would drop `code` and turn a safe list retry into a duplicate mutation.
  if (isDecisionFeatureError(error)) return error
  const apiError = isApiError(error) ? error : toApiError(error)
  return {
    operation,
    scope,
    status: apiError.status,
    code: apiError.backendKey,
    message: apiError.message,
    details: apiError.details,
  }
}

export interface DecisionsState extends FetchCache {
  requestedProjectId: string | null
  requestedKey: string | null
  loadedProjectId: string | null
  activeRequestId: string | null
  rows: DecisionView[]
  mutatingByRef: Record<string, boolean>
  error: DecisionFeatureError | null
  blocked: DecisionBlocked | null
  invalidationNonce: number
}

const initialState: DecisionsState = {
  requestedProjectId: null,
  requestedKey: null,
  loadedProjectId: null,
  activeRequestId: null,
  rows: [],
  mutatingByRef: {},
  error: null,
  blocked: null,
  invalidationNonce: 0,
  ...initialFetchCache,
}

/** The only decisions read. A new route key is never blocked by another key's in-flight request. */
export const openDecisions = createAsyncThunk<
  DecisionView[],
  ProjectArg,
  { rejectValue: DecisionFeatureError }
>(
  'decisions/open',
  async ({ orgId, projectId }, { rejectWithValue }) => {
    try {
      return await decisionsService.listDecisions(orgId, projectId)
    } catch (error) {
      return rejectWithValue(toDecisionError(error, 'list'))
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = (getState() as { decisions: DecisionsState }).decisions
      const key = cacheKeyOf(arg)
      // A route/tenant change is a fresh identity and must be allowed while
      // the previous identity is unresolved; the reducers reject its late result.
      if (state.requestedKey !== null && state.requestedKey !== key) return true
      return !state.loading && (state.invalidated || state.loadedKey !== key)
    },
  }
)

const rereadAfterMutation = async (
  arg: ProjectArg,
  dispatch: (action: unknown) => unknown,
  getState: () => unknown
): Promise<void> => {
  const state = (getState() as { decisions: DecisionsState }).decisions
  // Navigation has already made this entity irrelevant. Do not resurrect the
  // former project merely to settle an old form.
  if (state.requestedKey !== cacheKeyOf(arg)) return
  dispatch(decisionsSlice.actions.decisionsRefetchArmed())
  // `openDecisions.rejected` records a failure as an unscoped list error. A
  // completed mutation stays fulfilled, so the only retry the UI offers is a
  // safe reread rather than repeating the committed write.
  await dispatch(openDecisions(arg))
}

export const decideDecision = createAsyncThunk<
  DecisionView,
  ProjectArg & { decisionRef: string; input: DecisionDecideInput },
  { rejectValue: DecisionFeatureError }
>('decisions/decide', async ({ orgId, projectId, decisionRef, input }, api) => {
  let row: DecisionView
  try {
    row = await decisionsService.decideDecision(
      orgId,
      projectId,
      decisionRef,
      input
    )
  } catch (error) {
    return api.rejectWithValue(toDecisionError(error, 'decide', decisionRef))
  }
  await rereadAfterMutation({ orgId, projectId }, api.dispatch, api.getState)
  return row
})

export const supersedeDecision = createAsyncThunk<
  DecisionView,
  ProjectArg & { decisionRef: string; input: DecisionSupersedeInput },
  { rejectValue: DecisionFeatureError }
>(
  'decisions/supersede',
  async ({ orgId, projectId, decisionRef, input }, api) => {
    let row: DecisionView
    try {
      row = await decisionsService.supersedeDecision(
        orgId,
        projectId,
        decisionRef,
        input
      )
    } catch (error) {
      return api.rejectWithValue(
        toDecisionError(error, 'supersede', decisionRef)
      )
    }
    await rereadAfterMutation(
      { orgId, projectId },
      api.dispatch,
      api.getState
    )
    return row
  }
)

/** The shared activity nonce action carries the project scope for decisions too. */
export const decisionsBusFrame = createAction<{ projectId: string | null }>(
  'realtime/invalidateActivity'
)

export const decisionsSlice = createSlice({
  name: 'decisions',
  initialState,
  reducers: {
    decisionsClosed() {
      // The WHOLE slice returns to its initial state, `loading` included. A
      // close while a read is in flight (StrictMode's mount → unmount → mount)
      // orphans that read — its result is discarded by request id — so a
      // surviving `loading: true` would make the remount's guarded open skip
      // forever and the panel would never leave its loading state.
      return initialState
    },
    decisionsErrorDismissed(state, action: PayloadAction<string | undefined>) {
      if (action.payload === undefined || state.error?.scope === action.payload)
        state.error = null
    },
    decisionsRefetchArmed(state) {
      invalidate(state)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(openDecisions.pending, (state, action) => {
        const key = cacheKeyOf(action.meta.arg)
        if (state.requestedKey !== key) {
          state.rows = []
          state.loadedProjectId = null
          state.error = null
        } else if (state.error?.scope === undefined) {
          // A background refetch of the same project clears only the list-level
          // error; a row-scoped refusal belongs to that row's form and stays.
          state.error = null
        }
        fetchStarted(state)
        state.requestedProjectId = action.meta.arg.projectId
        state.requestedKey = key
        state.activeRequestId = action.meta.requestId
        state.blocked = null
      })
      .addCase(openDecisions.fulfilled, (state, action) => {
        const key = cacheKeyOf(action.meta.arg)
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedKey !== key
        )
          return
        state.rows = action.payload ?? []
        state.loadedProjectId = action.meta.arg.projectId
        state.blocked = null
        fetchSucceeded(state, key)
      })
      .addCase(openDecisions.rejected, (state, action) => {
        const key = cacheKeyOf(action.meta.arg)
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedKey !== key
        )
          return
        fetchFailed(state)
        const error = action.payload ?? toDecisionError(action.error, 'list')
        if (error.status === 403 || error.status === 404) {
          state.blocked = {
            kind: 'blocked',
            status: error.status,
            code: error.code,
            message: error.message,
          }
          state.error = null
        } else {
          state.error = error
          state.blocked = null
        }
      })
      .addCase(decideDecision.pending, (state, action) => {
        state.mutatingByRef[action.meta.arg.decisionRef] = true
        if (state.error?.scope === action.meta.arg.decisionRef)
          state.error = null
      })
      .addCase(decideDecision.fulfilled, (state, action) => {
        delete state.mutatingByRef[action.meta.arg.decisionRef]
      })
      .addCase(decideDecision.rejected, (state, action) => {
        delete state.mutatingByRef[action.meta.arg.decisionRef]
        state.error =
          action.payload ??
          toDecisionError(action.error, 'decide', action.meta.arg.decisionRef)
      })
      .addCase(supersedeDecision.pending, (state, action) => {
        state.mutatingByRef[action.meta.arg.decisionRef] = true
        if (state.error?.scope === action.meta.arg.decisionRef)
          state.error = null
      })
      .addCase(supersedeDecision.fulfilled, (state, action) => {
        delete state.mutatingByRef[action.meta.arg.decisionRef]
      })
      .addCase(supersedeDecision.rejected, (state, action) => {
        delete state.mutatingByRef[action.meta.arg.decisionRef]
        state.error =
          action.payload ??
          toDecisionError(
            action.error,
            'supersede',
            action.meta.arg.decisionRef
          )
      })
      .addCase(decisionsBusFrame, (state, action) => {
        if (
          action.payload.projectId !== null &&
          action.payload.projectId === state.requestedProjectId
        ) {
          state.invalidationNonce += 1
          invalidate(state)
        }
      })
  },
})

export const {
  decisionsClosed,
  decisionsErrorDismissed,
  decisionsRefetchArmed,
} = decisionsSlice.actions

interface WithDecisions {
  decisions: DecisionsState
}

export const selectDecisionRows = (state: WithDecisions): DecisionView[] =>
  state.decisions.rows
export const selectOpenDecisions = createSelector(
  [selectDecisionRows],
  (rows) => rows.filter((row) => row.status === 'OPEN')
)
export const selectDecisionsLoading = (state: WithDecisions): boolean =>
  state.decisions.loading
export const selectDecisionsError = (
  state: WithDecisions
): DecisionFeatureError | null => state.decisions.error
export const selectDecisionsBlocked = (
  state: WithDecisions
): DecisionBlocked | null => state.decisions.blocked
export const selectDecisionMutation =
  (decisionRef: string) =>
  (state: WithDecisions): boolean =>
    state.decisions.mutatingByRef[decisionRef] === true
export const selectDecisionsNonce = (state: WithDecisions): number =>
  state.decisions.invalidationNonce
